import { useCallback, useState } from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { columns, sortByDefault } from "./columns"
import { css, cx } from "@emotion/css"
import { defaultSort, limitAndOffset } from "api/utils"
import { deleteAgencyCompany, fetchAgencyCompanies } from "api/companies"
import { queryCache, useMutation, useQuery } from "react-query"
import { useHistory, useParams } from "react-router"

import { AddCompany } from "./AddModal"
import Button from "ui/buttons/Button"
import { ReactComponent as CompaniesIcon } from "assets/icon-companies.svg"
import { Company } from "models/company"
import { EmptyState } from "components/EmptyState"
import { Row } from "react-table"
import { Table } from "components/tables"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"
import { useToggle } from "hooks/useToggle"

// import { fetchMarketAgencyCompanies } from "api/agencies"

function Companies() {
  const { t } = useI18n()
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const { open, onOpen, onClose } = useToggle()
  const ls = useLocationSearch()
  const { hasAuthority } = useAuthenticated()

  const params = ls
    .map(limitAndOffset)
    .map(defaultSort(sortByDefault))
    .toString()

  const { data, isLoading } = useQuery(
    ["market-agency-companies", params, id],
    fetchAgencyCompanies,
  )

  const [deleteCompany] = useMutation(deleteAgencyCompany, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries(["market-agencies-companies-exclude"])
      queryCache.invalidateQueries(["market-agency-companies"])
      history.push(`/market-agencies/${id}/companies`)
    },
    onError: (e) => {
      setModalInfo(undefined)
    },
  })

  const onDelete = useCallback(
    (companyId: string) => {
      setModalInfo({
        title: t("company.list.delete.title"),
        type: "primary_destructive",
        description: t("company.list.delete.description"),
        submitLabel: t("company.list.delete.action"),
        onSubmit: () => {
          deleteCompany({ agencyId: id, companyId })
        },
      })
    },
    [id, deleteCompany, t],
  )

  const getRowProps = (row: Row<Company>) => {
    const id = row.original.id
    return {
      onClick: () => history.push(`/market-companies/${id}/profile`),
    }
  }

  const isModalOpen = Boolean(modalInfo)

  return (
    <>
      <Table<Company>
        id="company-list-table"
        columns={columns(t, onDelete, hasAuthority)}
        data={data?.data ?? []}
        filters={[]}
        loading={isLoading}
        total={data?.total ?? 0}
        searchBy="search"
        getRowProps={getRowProps}
        actionButton={
          hasAuthority(["AUTH_EDIT_COMPANY"]) && (
            <Button variant="contained" color="primary" onClick={onOpen}>
              {t("market-agency.company.create.button")}
            </Button>
          )
        }
        noResults={
          <EmptyState
            title={t("company.noresults.title")}
            subtitle={
              hasAuthority(["AUTH_EDIT_COMPANY"])
                ? t("company.noresults.subtitle")
                : undefined
            }
            icon={<CompaniesIcon />}
          >
            {hasAuthority(["AUTH_EDIT_COMPANY"]) ? (
              <span
                onClick={onOpen}
                className={cx(
                  "BodyBody-1Primary-mainRegular",
                  styles.createNew,
                )}
              >
                {t("company.add_button")}
              </span>
            ) : null}
          </EmptyState>
        }
      />
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
      {open ? <AddCompany open={open} onClose={onClose} /> : null}
    </>
  )
}

export default Companies

const styles = {
  createNew: css`
    padding-top: 1px;
    margin: 0 0.8ch;
    cursor: pointer;
  `,
}
